import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Plus, X, Repeat, ArrowRight, Trash2, Loader2, Check, Package, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

type VendaBusca = {
  pedido_id: number; numero: number; status: string; cliente_id: number; cliente_nome: string
  criado_em: string; total: number
  itens: { pedido_item_id: number; produto_id: number; descricao: string; quantidade: number; ja_devolvido: number; preco_unitario: number }[]
}
type ItemNovo = { produto_id: number; descricao: string; codigo: string; quantidade: number; preco_unitario: number }

export default function TrocasPage() {
  const [aba, setAba] = useState<'nova' | 'historico'>('nova')
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-ink-900 flex items-center gap-2"><Repeat size={22} className="text-brand-600" /> Trocas de Produtos</h2>
        <div className="flex gap-1 bg-ink-100 rounded-lg p-1">
          <button onClick={() => setAba('nova')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${aba === 'nova' ? 'bg-white shadow text-brand-700' : 'text-ink-600'}`}>Nova troca</button>
          <button onClick={() => setAba('historico')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${aba === 'historico' ? 'bg-white shadow text-brand-700' : 'text-ink-600'}`}>Histórico</button>
        </div>
      </div>
      {aba === 'nova' ? <NovaTroca /> : <Historico />}
    </div>
  )
}

function NovaTroca() {
  const qc = useQueryClient()
  const [buscaNum, setBuscaNum] = useState('')
  const [venda, setVenda] = useState<VendaBusca | null>(null)
  const [resultados, setResultados] = useState<VendaBusca[]>([])
  const [devolver, setDevolver] = useState<Record<number, number>>({}) // pedido_item_id -> qtd
  const [novos, setNovos] = useState<ItemNovo[]>([])
  const [motivo, setMotivo] = useState('')
  const [reembolso, setReembolso] = useState<'dinheiro' | 'credito'>('credito')
  const [formaPaga, setFormaPaga] = useState('dinheiro')
  const [depositoId, setDepositoId] = useState<number | ''>('')
  const [confirmando, setConfirmando] = useState(false)  // FEATURE 03/07: confirma antes de executar
  // FEATURE 03/07: "Consumidor Final" genérico não pode receber CRÉDITO na loja (some no limbo).
  const clienteGenerico = !!venda && (venda.cliente_nome || '').trim().toLowerCase() === 'consumidor final'
  // Força dinheiro quando o cliente é genérico (crédito exige cliente identificado).
  useEffect(() => { if (clienteGenerico && reembolso === 'credito') setReembolso('dinheiro') }, [clienteGenerico])

  const { data: depositos = [] } = useQuery<any[]>({ queryKey: ['pdv-depositos-venda'], queryFn: async () => (await api.get('/pdv/depositos-venda')).data })
  const { data: formas = [] } = useQuery<any[]>({ queryKey: ['cfg-formas'], queryFn: async () => (await api.get('/caixa/config-formas')).data })

  async function buscar() {
    if (!buscaNum.trim()) return
    try {
      const { data } = await api.get('/vendas/trocas/buscar-venda', { params: { numero: Number(buscaNum) } })
      if (!data.length) { toast.error('Nenhuma venda encontrada com esse número'); setResultados([]); return }
      if (data.length === 1) selecionarVenda(data[0]); else setResultados(data)
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Erro ao buscar venda') }
  }
  function selecionarVenda(v: VendaBusca) {
    setVenda(v); setResultados([])
    const ini: Record<number, number> = {}
    v.itens.forEach(it => { ini[it.pedido_item_id] = 0 })
    setDevolver(ini)
  }
  function resetar() { setVenda(null); setDevolver({}); setNovos([]); setMotivo(''); setBuscaNum('') }

  const dep = depositoId || depositos[0]?.id
  const valorDevolvido = venda ? venda.itens.reduce((s, it) => s + (devolver[it.pedido_item_id] || 0) * it.preco_unitario, 0) : 0
  const valorNovo = novos.reduce((s, n) => s + n.quantidade * n.preco_unitario, 0)
  const diferenca = valorNovo - valorDevolvido
  const algumDevolvido = Object.values(devolver).some(q => q > 0)

  const confirmar = useMutation({
    mutationFn: async () => {
      const itens_devolvidos = venda!.itens
        .filter(it => (devolver[it.pedido_item_id] || 0) > 0)
        .map(it => ({ pedido_item_id: it.pedido_item_id, quantidade: devolver[it.pedido_item_id] }))
      const body: any = {
        pedido_origem_id: venda!.pedido_id,
        itens_devolvidos,
        itens_novos: novos.map(n => ({ produto_id: n.produto_id, quantidade: n.quantidade, preco_unitario: n.preco_unitario })),
        motivo, deposito_id: dep || undefined, filial_id: undefined,
        reembolso,
        pagamentos_diferenca: diferenca > 0 ? [{ forma: formaPaga, valor: diferenca }] : [],
      }
      return (await api.post('/vendas/trocas', body)).data
    },
    onSuccess: (r) => {
      const msg = r.sentido === 'cliente_paga' ? `Cliente paga ${formatMoney(Math.abs(r.diferenca))}`
        : r.sentido === 'cliente_recebe' ? `Cliente recebe ${formatMoney(Math.abs(r.diferenca))} (${r.reembolso})`
        : 'Troca sem diferença'
      toast.success(`Troca registrada — ${msg}`)
      qc.invalidateQueries({ queryKey: ['trocas-hist'] })
      qc.invalidateQueries({ queryKey: ['pdv-vendas-do-dia'] })
      setConfirmando(false)
      resetar()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao registrar troca'),
  })

  const formasPaga = (formas.length ? formas : [{ forma: 'dinheiro', label: 'Dinheiro' }]).filter((f: any) => f.ativo !== false)

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* COLUNA 1: venda original + itens devolvidos */}
      <div className="space-y-4">
        <div className="card card-body">
          <div className="text-xs font-semibold text-ink-500 uppercase mb-2">1. Venda original</div>
          {!venda ? (
            <>
              <div className="flex gap-2">
                <input value={buscaNum} onChange={e => setBuscaNum(e.target.value)} onKeyDown={e => e.key === 'Enter' && buscar()}
                  placeholder="Número da venda" className="input flex-1" type="number" />
                <button onClick={buscar} className="btn-primary"><Search size={16} /> Buscar</button>
              </div>
              {resultados.length > 0 && (
                <div className="mt-2 border rounded-lg divide-y max-h-60 overflow-auto">
                  {resultados.map(v => (
                    <button key={v.pedido_id} onClick={() => selecionarVenda(v)} className="block w-full text-left px-3 py-2 hover:bg-brand-50 text-sm">
                      <b>#{v.numero}</b> · {v.cliente_nome} · {formatMoney(v.total)}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div><span className="font-bold">Venda #{venda.numero}</span> <span className="text-sm text-ink-500">· {venda.cliente_nome}</span></div>
                <button onClick={resetar} className="text-xs text-ink-400 hover:text-rose-600 flex items-center gap-1"><X size={12} /> trocar venda</button>
              </div>
              <div className="text-xs font-semibold text-ink-500 uppercase mb-1">Itens que o cliente devolve</div>
              <div className="space-y-1.5">
                {venda.itens.map(it => {
                  const max = it.quantidade - it.ja_devolvido
                  const qtd = devolver[it.pedido_item_id] || 0
                  return (
                    <div key={it.pedido_item_id} className={`flex items-center gap-2 p-2 rounded-lg border ${qtd > 0 ? 'border-brand-300 bg-brand-50' : 'border-ink-200'} ${max <= 0 ? 'opacity-50' : ''}`}>
                      <input type="checkbox" disabled={max <= 0} checked={qtd > 0}
                        onChange={e => setDevolver(d => ({ ...d, [it.pedido_item_id]: e.target.checked ? max : 0 }))} className="accent-brand-600" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{it.descricao}</div>
                        <div className="text-[11px] text-ink-500">{formatMoney(it.preco_unitario)} · vendido {it.quantidade}{it.ja_devolvido > 0 ? ` · já devolvido ${it.ja_devolvido}` : ''}</div>
                      </div>
                      {max > 0 && qtd > 0 && (
                        <input type="number" min={1} max={max} value={qtd} onChange={e => setDevolver(d => ({ ...d, [it.pedido_item_id]: Math.min(max, Math.max(1, Number(e.target.value) || 1)) }))} className="input !py-1 w-16 text-center" />
                      )}
                      {max <= 0 && <span className="text-[10px] text-ink-400">devolvido</span>}
                    </div>
                  )
                })}
              </div>
              <div className="mt-2 text-right text-sm">Valor devolvido: <b className="text-brand-700">{formatMoney(valorDevolvido)}</b></div>
            </div>
          )}
        </div>
      </div>

      {/* COLUNA 2: produtos novos + acerto */}
      <div className="space-y-4">
        <div className="card card-body">
          <div className="text-xs font-semibold text-ink-500 uppercase mb-2">2. Produtos que o cliente leva</div>
          <BuscaProdutoNovo onAdd={p => setNovos(n => [...n, p])} />
          <div className="space-y-1.5 mt-2">
            {novos.length === 0 && <div className="text-xs text-ink-400">Nenhum produto novo. (Pode ser só devolução/crédito.)</div>}
            {novos.map((n, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 rounded-lg border border-ink-200">
                <div className="flex-1 min-w-0"><div className="text-sm truncate">{n.descricao}</div><div className="text-[11px] text-ink-500 font-mono">{n.codigo}</div></div>
                <input type="number" min={1} value={n.quantidade} onChange={e => setNovos(arr => arr.map((x, i) => i === idx ? { ...x, quantidade: Math.max(1, Number(e.target.value) || 1) } : x))} className="input !py-1 w-14 text-center" />
                <input type="number" step="0.01" value={n.preco_unitario} onChange={e => setNovos(arr => arr.map((x, i) => i === idx ? { ...x, preco_unitario: Number(e.target.value) || 0 } : x))} className="input !py-1 w-24 text-right" />
                <button onClick={() => setNovos(arr => arr.filter((_, i) => i !== idx))} className="text-ink-400 hover:text-rose-600"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          <div className="mt-2 text-right text-sm">Valor novo: <b className="text-ink-900">{formatMoney(valorNovo)}</b></div>
        </div>

        {/* Acerto */}
        <div className="card card-body">
          <div className="text-xs font-semibold text-ink-500 uppercase mb-2">3. Acerto</div>
          <div className={`rounded-lg p-3 mb-3 text-center ${diferenca > 0 ? 'bg-amber-50 text-amber-800' : diferenca < 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-ink-50 text-ink-600'}`}>
            {diferenca > 0 ? <>Cliente <b>paga</b> a diferença de <b>{formatMoney(diferenca)}</b></>
              : diferenca < 0 ? <>Cliente <b>recebe</b> <b>{formatMoney(Math.abs(diferenca))}</b></>
                : <>Troca sem diferença de valor</>}
          </div>
          {diferenca > 0 && (
            <div className="mb-3">
              <label className="label">Forma de pagamento da diferença</label>
              <select value={formaPaga} onChange={e => setFormaPaga(e.target.value)} className="input">
                {formasPaga.map((f: any) => <option key={f.forma} value={f.forma}>{f.label}</option>)}
              </select>
            </div>
          )}
          {diferenca < 0 && (
            <div className="mb-3">
              <label className="label">Como devolver pro cliente</label>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setReembolso('dinheiro')} className={`py-2 rounded-lg border-2 text-sm font-medium ${reembolso === 'dinheiro' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-ink-200'}`}>Dinheiro (troco)</button>
                <button onClick={() => !clienteGenerico && setReembolso('credito')} disabled={clienteGenerico}
                  title={clienteGenerico ? 'Crédito exige cliente identificado' : ''}
                  className={`py-2 rounded-lg border-2 text-sm font-medium ${reembolso === 'credito' ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-ink-200'} ${clienteGenerico ? 'opacity-40 cursor-not-allowed' : ''}`}>Crédito na loja</button>
              </div>
              {clienteGenerico && (
                <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                  <span>Venda do <b>Consumidor Final</b> — crédito na loja precisa de um cliente identificado. Vai como <b>dinheiro (troco)</b>. Pra dar crédito, vincule o cliente à venda antes.</span>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="label">Depósito de saída</label>
              <select value={depositoId} onChange={e => setDepositoId(Number(e.target.value))} className="input">
                {depositos.map((d: any) => <option key={d.id} value={d.id}>{d.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Motivo</label>
              <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ex: desistência, defeito" className="input" />
            </div>
          </div>
          <button onClick={() => setConfirmando(true)} disabled={!venda || !algumDevolvido || confirmar.isPending}
            className="btn-primary w-full justify-center py-2.5 disabled:opacity-50">
            {confirmar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Revisar e confirmar
          </button>
          {!algumDevolvido && venda && <div className="text-[11px] text-ink-400 text-center mt-1">Marque ao menos um item devolvido.</div>}
        </div>
      </div>

      {/* FEATURE 03/07: confirmação antes de executar a troca (resumo do que vai acontecer) */}
      {confirmando && venda && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setConfirmando(false)}>
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-ink-200 flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2"><Repeat size={18} className="text-brand-600" /> Confirmar troca</h3>
              <button onClick={() => setConfirmando(false)} className="text-ink-400 hover:text-ink-700"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="text-ink-600">Venda <b>#{venda.numero}</b> · {venda.cliente_nome}</div>
              <div>
                <div className="text-[11px] font-semibold text-ink-500 uppercase mb-1">Cliente devolve</div>
                <ul className="space-y-0.5">
                  {venda.itens.filter(it => (devolver[it.pedido_item_id] || 0) > 0).map(it => (
                    <li key={it.pedido_item_id} className="text-ink-800">• {devolver[it.pedido_item_id]}× {it.descricao}</li>
                  ))}
                </ul>
              </div>
              {novos.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-ink-500 uppercase mb-1">Cliente leva</div>
                  <ul className="space-y-0.5">
                    {novos.map((n, i) => <li key={i} className="text-ink-800">• {n.quantidade}× {n.descricao} ({formatMoney(n.preco_unitario)})</li>)}
                  </ul>
                </div>
              )}
              <div className="rounded-lg bg-ink-50 p-2.5 space-y-1">
                <div className="flex justify-between"><span className="text-ink-500">Devolvido</span><b>{formatMoney(valorDevolvido)}</b></div>
                <div className="flex justify-between"><span className="text-ink-500">Novo</span><b>{formatMoney(valorNovo)}</b></div>
                <div className="flex justify-between border-t border-ink-200 pt-1">
                  <span className="text-ink-700 font-medium">
                    {diferenca > 0 ? 'Cliente paga' : diferenca < 0 ? 'Cliente recebe' : 'Sem diferença'}
                  </span>
                  <b className={diferenca > 0 ? 'text-amber-700' : diferenca < 0 ? 'text-emerald-700' : 'text-ink-500'}>
                    {formatMoney(Math.abs(diferenca))}
                    {diferenca > 0 ? ` · ${formaPaga}` : diferenca < 0 ? ` · ${reembolso === 'credito' ? 'crédito' : 'dinheiro'}` : ''}
                  </b>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-ink-200 flex justify-end gap-2">
              <button onClick={() => setConfirmando(false)} className="btn-secondary">Voltar</button>
              <button onClick={() => confirmar.mutate()} disabled={confirmar.isPending} className="btn-primary">
                {confirmar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Confirmar troca
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function BuscaProdutoNovo({ onAdd }: { onAdd: (p: ItemNovo) => void }) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const { data: produtos = [] } = useQuery<any[]>({
    queryKey: ['troca-prod', q], enabled: open && q.length >= 2,
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { q, limit: 15 } })).data,
  })
  return (
    <div className="relative">
      <div className="flex items-center gap-1.5 border rounded-lg px-2">
        <Search size={14} className="text-ink-400" />
        <input value={q} onChange={e => { setQ(e.target.value); setOpen(true) }}
          onKeyDown={enterAbrePrimeiro(open && q.length >= 2 ? produtos : [], (p: any) => { onAdd({ produto_id: p.id, descricao: p.descricao, codigo: p.codigo, quantidade: 1, preco_unitario: Number(p.preco_venda || 0) }); setQ(''); setOpen(false) })}
          placeholder="Buscar produto que o cliente vai levar…" className="flex-1 py-2 text-sm outline-none bg-transparent" />
      </div>
      {open && q.length >= 2 && produtos.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow max-h-52 overflow-auto">
          {produtos.map((p: any) => (
            <button key={p.id} onClick={() => { onAdd({ produto_id: p.id, descricao: p.descricao, codigo: p.codigo, quantidade: 1, preco_unitario: Number(p.preco_venda || 0) }); setQ(''); setOpen(false) }}
              className="block w-full text-left px-3 py-2 hover:bg-brand-50 text-sm">
              <span className="font-mono text-xs text-ink-400 mr-1">{p.codigo}</span>{p.descricao}
              <span className="float-right text-ink-500">{formatMoney(p.preco_venda || 0)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function Historico() {
  // FEATURE 03/07: sempre re-busca ao abrir a aba (o histórico "não atualizava" após uma troca).
  const { data: trocas = [], isLoading } = useQuery<any[]>({
    queryKey: ['trocas-hist'],
    queryFn: async () => (await api.get('/vendas/trocas')).data,
    refetchOnMount: 'always',
  })
  if (isLoading) return <div className="flex items-center justify-center py-10 text-ink-400"><Loader2 className="animate-spin mr-2" size={18} /> Carregando…</div>
  if (!trocas.length) return <div className="text-center py-10 text-ink-400"><Package size={36} className="mx-auto opacity-30 mb-2" />Nenhuma troca registrada ainda.</div>
  return (
    <div className="card overflow-hidden">
      <table className="table">
        <thead><tr><th>Data</th><th>Cliente</th><th>Venda orig.</th><th>Venda nova</th><th className="text-right">Devolvido</th><th className="text-right">Novo</th><th className="text-right">Diferença</th><th>Acerto</th></tr></thead>
        <tbody>
          {trocas.map(t => (
            <tr key={t.id}>
              <td className="whitespace-nowrap text-xs">{t.criado_em ? new Date(t.criado_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
              <td>{t.cliente_nome}</td>
              <td className="font-mono">#{t.pedido_origem_numero ?? t.pedido_origem_id}</td>
              <td className="font-mono">{t.pedido_novo_numero ? `#${t.pedido_novo_numero}` : '—'}</td>
              <td className="text-right">{formatMoney(t.valor_devolvido)}</td>
              <td className="text-right">{formatMoney(t.valor_novo)}</td>
              <td className={`text-right font-semibold ${t.diferenca > 0 ? 'text-amber-700' : t.diferenca < 0 ? 'text-emerald-700' : 'text-ink-500'}`}>
                {t.diferenca > 0 ? '+' : ''}{formatMoney(t.diferenca)}
              </td>
              <td className="text-xs">
                {t.sentido === 'cliente_paga' ? <span className="text-amber-700">paga ({t.forma_acerto})</span>
                  : t.sentido === 'cliente_recebe' ? <span className="text-emerald-700">recebe ({t.forma_acerto})</span>
                    : <span className="text-ink-400">seca</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
